import * as React from "react";
import { Button, Flex, TextField } from "@aws-amplify/ui-react";
import { Container, Card, Modal } from 'react-bootstrap';
import { useNavigate, useLocation } from "react-router-dom";
import { playDrumrollAudio } from "../audioManager";
import { updateActiveGame, removeActiveGame } from "../lib/activeGameService";
import { DataStore, Auth } from "aws-amplify";
import { Game, Player } from "../models";

export default function GolfScorecard() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeGameIdFromState = location.state?.activeGameId || location.state?.activeGameSnapshot?.activeGameId || null;
  const [players, setPlayers] = React.useState(location.state?.activeGameSnapshot?.players || location.state?.players || []);
  const [initialDealer] = React.useState(location.state?.activeGameSnapshot?.initialDealer || location.state?.initialDealer || (players[0]?.name || ""));
  const [currentRound, setCurrentRound] = React.useState(location.state?.activeGameSnapshot?.currentRound || 1);
  const [activeGameId] = React.useState(activeGameIdFromState);
  const [roundScores, setRoundScores] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [completedRounds, setCompletedRounds] = React.useState(location.state?.activeGameSnapshot?.completedRounds || []);
  const [showRoundSummary, setShowRoundSummary] = React.useState(false);
  const [lastCompletedRound, setLastCompletedRound] = React.useState(null);
  const [showEndGameConfirm, setShowEndGameConfirm] = React.useState(false);

  // Initialize round scores object
  React.useEffect(() => {
    const initialScores = {};
    players.forEach(player => {
      initialScores[player.id] = "";
    });
    setRoundScores(initialScores);
  }, [players]);

  // Play drum roll when modal opens
  React.useEffect(() => {
    if (showRoundSummary) {
      playDrumRoll();
    }
  }, [showRoundSummary]);

  const playDrumRoll = async () => {
    await playDrumrollAudio();
  };

  const getDealerForRound = (roundNumber) => {
    // Find the index of the initial dealer
    const dealerIndex = players.findIndex(p => p.name === initialDealer);
    if (dealerIndex === -1) return players[0]?.name || "";
    
    // Calculate dealer for this round (rotates to next player each round)
    const adjustedIndex = (dealerIndex + roundNumber - 1) % players.length;
    return players[adjustedIndex].name;
  };

  const handleScoreChange = (playerId, value) => {
    setRoundScores({
      ...roundScores,
      [playerId]: value
    });

    // Validate score
    if (value === "") {
      const newErrors = { ...errors };
      delete newErrors[playerId];
      setErrors(newErrors);
    } else {
      const score = parseInt(value);
      if (isNaN(score) || score < -8 || score > 60) {
        setErrors({
          ...errors,
          [playerId]: "Score must be between -8 and 60"
        });
      } else {
        const newErrors = { ...errors };
        delete newErrors[playerId];
        setErrors(newErrors);
      }
    }
  };

  const isRoundValid = () => {
    // Check all players have a score
    for (let i = 0; i < players.length; i++) {
      const playerId = players[i].id;
      if (roundScores[playerId] === "" || roundScores[playerId] === undefined) {
        return false;
      }
    }
    // Check no errors
    return Object.keys(errors).length === 0;
  };

  const handleRecordRound = (event) => {
    event.preventDefault();
    
    if (!isRoundValid()) {
      alert("Please fill in all scores correctly before recording the round");
      return;
    }

    // Create round data
    const roundData = {
      roundNumber: currentRound,
      scores: {}
    };

    // Update players with new scores
    const updatedPlayers = players.map(player => {
      const score = parseInt(roundScores[player.id]);
      roundData.scores[player.id] = { name: player.name, score };
      
      return {
        ...player,
        scores: [...player.scores, score],
        total: player.total + score
      };
    });

    const newCompleted = [...completedRounds, roundData];
    setPlayers(updatedPlayers);
    setCompletedRounds(newCompleted);
    setLastCompletedRound(roundData);
    setShowRoundSummary(true);

    try {
      if (activeGameId) {
        updateActiveGame(activeGameId, {
          players: updatedPlayers,
          lastRound: roundData,
          snapshot: { players: updatedPlayers, currentRound: currentRound + 1, completedRounds: newCompleted }
        });
      }
    } catch (e) {
      // ignore update errors
    }
  };

  const handleCloseSummary = () => {
    setShowRoundSummary(false);
    
    // Reset for next round
    const nextRoundScores = {};
    players.forEach(player => {
      nextRoundScores[player.id] = "";
    });
    setRoundScores(nextRoundScores);
    setCurrentRound(currentRound + 1);
  };

  const handleEndGame = (event) => {
    event.preventDefault();
    setShowEndGameConfirm(true);
  };

  // Sort players by total score (lowest is best in Golf)
  const sortedPlayers = [...players].sort((a, b) => a.total - b.total);

  return (
    <Container className="mt-3">
      {/* Current Round Input Section */}
      <Card className="mb-4 sticky-top" style={{ top: "10px", zIndex: 100 }}>
        <Card.Body>
          <Card.Title>Round {currentRound} - Dealer: {getDealerForRound(currentRound)}</Card.Title>
          <Card.Text className="mb-3">Enter scores for each player (-8 to 60)</Card.Text>

          <Flex direction="column" gap="12px" className="mb-3">
            {players.map(player => (
              <div key={player.id}>
                <label htmlFor={`score-${player.id}`} className="form-label">
                  {player.name}
                </label>
                <TextField
                  id={`score-${player.id}`}
                  type="number"
                  placeholder="Enter score"
                  value={roundScores[player.id] || ""}
                  onChange={(e) => handleScoreChange(player.id, e.target.value)}
                  hasError={!!errors[player.id]}
                  errorMessage={errors[player.id]}
                  min="-8"
                  max="60"
                />
              </div>
            ))}
          </Flex>

          <Flex gap="10px">
            <Button
              children="Record Round"
              onClick={handleRecordRound}
              variation="primary"
              isDisabled={!isRoundValid()}
            />
            <Button
              children="End Game"
              className="end-game-button"
              onClick={handleEndGame}
              variation="default"
            />
          </Flex>
        </Card.Body>
      </Card>

      {/* Running Totals Section */}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Current Totals</Card.Title>
          <div>
            {sortedPlayers.map((player, index) => (
              <div
                key={player.id}
                className="score-list-row d-flex justify-content-between align-items-center mb-2"
              >
                <span>
                  {index + 1}. {player.name}
                </span>
                <span style={{ fontSize: "1.3rem", fontWeight: "bold", color: "#0d6efd" }}>{player.total}</span>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Completed Rounds Section */}
      {completedRounds.length > 0 && (
        <div className="mb-4">
          <h5 className="mb-3">Round History</h5>
          {completedRounds.map(round => (
            <Card key={round.roundNumber} className="mb-3">
              <Card.Body>
                <Card.Title className="mb-3">Round {round.roundNumber}</Card.Title>
                <div>
                  {players.map(player => (
                    <div
                      key={player.id}
                      className="score-list-row d-flex justify-content-between align-items-center mb-1"
                    >
                      <span>{player.name}:</span>
                      <span style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#6c757d" }}>
                        {round.scores[player.id].score}
                      </span>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state */}
      {completedRounds.length === 0 && (
        <Card className="text-center mb-4">
          <Card.Body>
            <p className="text-muted">No rounds recorded yet. Enter scores above to begin!</p>
          </Card.Body>
        </Card>
      )}

      {/* Round Summary Modal */}
      {lastCompletedRound && (
        <Modal show={showRoundSummary} onHide={handleCloseSummary} centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "1.65rem" }}>Round {lastCompletedRound.roundNumber} Results</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: "1rem", fontSize: "1.1rem" }}>
            {/* Round Rankings */}
            <h6 className="mb-2" style={{ fontSize: "1.25rem", fontWeight: "bold" }}>Round Scores</h6>
            {Object.entries(lastCompletedRound.scores)
              .map(([playerId, scoreData]) => ({
                playerId,
                name: scoreData.name,
                score: scoreData.score
              }))
              .sort((a, b) => a.score - b.score)
              .map((playerScore, index) => (
                <div key={playerScore.playerId} className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: "1.6rem" }}>
                  <span>
                    <strong>{index + 1}.</strong> {playerScore.name}
                  </span>
                  <span style={{ fontSize: "1.4rem", fontWeight: "bold", color: "#000000" }}>{playerScore.score}</span>
                </div>
              ))}
            
            {/* Next Dealer */}
            <div className="mt-2 border-top" style={{ paddingTop: "0.5rem" }}>
              <div className="d-flex justify-content-between align-items-center">
                <h6 style={{ fontSize: "1.25rem", fontWeight: "bold", marginBottom: "0" }}>Next Dealer</h6>
                <p style={{ marginBottom: "0", fontSize: "1.5rem" }}>
                  <strong>{getDealerForRound(currentRound + 1)}</strong>
                </p>
              </div>
            </div>

            {/* Overall Standings */}
            <div className="mt-2 border-top" style={{ paddingTop: "0.5rem" }}>
              <h6 className="mb-2" style={{ fontSize: "1.25rem", fontWeight: "bold" }}>Overall Standings</h6>
              {sortedPlayers.map((player, index) => (
                <div key={player.id} className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: "1.6rem" }}>
                  <span>
                    <strong>{index + 1}.</strong> {player.name}
                  </span>
                  <span style={{ fontSize: "1.4rem", fontWeight: "bold", color: "#198754" }}>{player.total}</span>
                </div>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer style={{ padding: "0.75rem" }}>
            <Button onClick={handleCloseSummary} variation="primary">
              Next Round
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* End Game Confirmation Modal */}
      <Modal show={showEndGameConfirm} onHide={() => setShowEndGameConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>End Game?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to end the game?
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowEndGameConfirm(false)} className="btn btn-secondary">
            Cancel
          </Button>
            <Button onClick={async () => {
              setShowEndGameConfirm(false);
              try {
                // persist Game and Player records so they appear in Game History
                const user = await Auth.currentAuthenticatedUser();
                const owner = user.username;
                const gameModel = new Game({ GameDate: new Date().toISOString(), GameLead: owner, GameType: 'Golf' });
                const savedGame = await DataStore.save(gameModel);
                for (const p of players) {
                  const playerModel = new Player({ Name: p.name || p.Name || '', gameID: savedGame.id, total: p.total || 0 });
                  await DataStore.save(playerModel);
                }
                // gameType is stored on the Game model now; no sentinel required
              } catch (e) {
                console.error('Error saving Golf game to history', e);
              }
              try { if (activeGameId) removeActiveGame(activeGameId); } catch(e) {}
              navigate("/golf-results", {
                state: {
                  players: players,
                  completedRounds: completedRounds
                }
              });
            }} className="btn btn-primary">
              End Game
            </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
